import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FullscreenReviewShellProps {
  /** Primary identifier shown in the top bar (e.g. the document number). */
  title: ReactNode;
  /** Optional secondary line (e.g. customer / project). */
  subtitle?: ReactNode;
  /** Exit the fullscreen review — wired to each module's own review-mode setter. */
  onExit: () => void;
  children: ReactNode;
}

// 86d3fcbte — shared chrome for full-bleed review mode (Quotation + Sales Order).
//
// Renders the source ↔ extracted-lines split as a viewport-filling overlay so the
// review surface is no longer squeezed between the app sidebar and the draft list —
// both are simply covered. Presentational only: each module keeps its own review
// state and its own resize-split logic; this just provides the cover + a thin
// top bar with the doc title and an Exit control.
//
// Why a bespoke `fixed inset-0` portal and NOT shadcn Dialog: Dialog's focus-trap,
// scroll-lock and dimmed backdrop fight the drag-to-resize handle and the nested
// Radix select / popover editors inside the line-items list. Portaling to
// document.body also sidesteps any containing-block trap (a `transform` /
// `backdrop-filter` ancestor would otherwise pin a `fixed` child to that ancestor
// instead of the viewport).
export function FullscreenReviewShell({
  title,
  subtitle,
  onExit,
  children,
}: FullscreenReviewShellProps) {
  // Esc closes the overlay — but only when nothing else owns the key. If an
  // inline cell editor or a Radix select/popover is open, that layer consumes
  // Esc first (it preventDefaults, or it mounts a popper wrapper), so we bail
  // and let the user's Esc dismiss the editor rather than the whole overlay.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      onExit();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onExit]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b bg-background px-4">
        <div className="min-w-0">
          <div className="truncate font-mono text-sm font-medium text-foreground">{title}</div>
          {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExit}
          title="Exit review (Esc)"
          className="shrink-0"
        >
          <X className="mr-1.5 h-3.5 w-3.5" />
          Exit review
        </Button>
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>,
    document.body,
  );
}
